/*!
  Pioneer WFRIA ListBuilder v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ListBuilder"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ListBuilder") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ListBuilder");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { requiredIf, validChild } from "../common/proptypes";
import Table from "../Table/Table";
import { EDIT, EDITSAVE, REORDER, REORDERSAVE, TOP, UP } from "./constants";
import EditPanel from "./EditPanel/EditPanel";
import KeyboardHelpPanel from "./ReorderPanel/KeyboardHelpPanel/KeyboardHelpPanel";
import ReorderPanel from "./ReorderPanel/ReorderPanel";
import { getStringsMemoized, handleTableCellFocus } from "./utils";
/**
 * The ListBuilder is a React component which allows users to view items, make selections
 * and reorder the items in the sequence the user prefers.
 *
 * NOTE: Although Table component is fully customizable few props like,
 * `autoWidth`, `caption`, `controlled` etc., are not to be passed.
 * Passing these props might lead to unexpected behaviour of ListBuilder.
 * Example: passing `autoWidth` might result in `Table` rendering issues.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function ListBuilder(_ref) {
  let {
    openEdit,
    openReorder,
    children,
    elementToFocusOnClose,
    minSelection,
    maxSelection,
    customStrings,
    findByContainerAriaLabel,
    onClose,
    afterSave,
    onToggle,
    findBy,
    reorderTableName,
    editPanelHeaderDockAriaLabel,
    reorderPanelHeaderDockAriaLabel,
    panelWidth,
    reorderPanelEditButtonHidden,
    disableButtons
  } = _ref;
  const initialSelectedRows = children.props.selectable && children.props.selectable.selectedRows; // To enable/disable move down/bottom buttons.

  const [disableMoveDownBottomButtons, setDisableMoveDownBottomButtons] = useState(true); // To enable/disable move top/up buttons.

  const [disableMoveUpTopButtons, setDisableMoveUpTopButtons] = useState(true); // Holds current selections of Edit panel.

  const [selectedRows, setSelectedRows] = useState(initialSelectedRows || []); // Holds last saved selections, in case user cancels current selections.

  const [lastSavedSelectedRows, setLastSavedSelectedRows] = useState(initialSelectedRows || []); // Holds reorderpanel's selections.

  const [reorderSelectedRows, setReorderSelectedRows] = useState([]); // To hold order of rows when move up/down actions are performed.

  const [reorderPanelData, setReorderPanelData] = useState(initialSelectedRows || []); // To store the records which matching the search criteria.

  const [findByFilteredData, setFindByFilteredData] = useState(null); // Holds last saved Reorder panel data.

  const [lastSavedReorderPanelData, setLastSavedReorderPanelData] = useState(initialSelectedRows || []); // Holds the value of the input field.

  const [findByLookUpString, setFindByLookUpString] = useState(""); // Track the state of ReorderPanel's keyboard help panel.

  const [keyboardInstructionPanelIsOpen, setKeyboardInstructionPanelIsOpen] = useState(false); // Run useEffect to focus table cell after `handleClearSelections`.

  const [focusTableCell, setFocusTableCell] = useState(false); // Ensures minimum selections are made, at least once, before showing error.

  const minSelectedRef = useRef(false); // Ensures maximum selections are made, at least once, before showing error.

  const maxSelectedRef = useRef(false);
  const elementToFocusOnHelpPanelCloseRef = useRef(null);
  const inputElementRef = useRef();
  const reorderPanelNodeRef = useRef();
  const firstPanelClosingCheckRef = useRef(true);
  const strings = getStringsMemoized(customStrings, minSelection, maxSelection);
  useEffect(() => {
    // Don't run on initial render.
    if (firstPanelClosingCheckRef.current) {
      firstPanelClosingCheckRef.current = false;
      return;
    }
    /**
     * Below is to determine the element which needs to be focused
     * when any of Edit or Reorder Panels was previously in `open` state and
     * currently both of the Panels state is `close`.
     */


    let toFocusOnClose = elementToFocusOnClose; // If true, either Edit or Reorder Panel was in Open state.

    const panelWasInOpenState = !openEdit || !openReorder; // If true, both Edit and Reorder Panels are in closed state.

    const panelIsClosed = !openEdit && !openReorder;

    if (panelWasInOpenState && panelIsClosed) {
      if (typeof elementToFocusOnClose === "string") {
        toFocusOnClose = document.querySelector(elementToFocusOnClose);
      } else {
        toFocusOnClose = elementToFocusOnClose();
      }

      toFocusOnClose && toFocusOnClose.focus();
    }
  }, [openEdit, openReorder, elementToFocusOnClose]);
  /**
   * @description This method is used to store the
   * native input node of the field.
   */

  const setRootRef = inputNode => {
    inputElementRef.current = inputNode;
  }; // Used for filtering the selected rows to feed Reorder panel and pass data to consumer.


  const getReorderData = () => {
    const {
      rowKey,
      data
    } = children.props;
    /* eslint react/prop-types: 0 */
    // To avoid below code execution when there are no selected rows.

    if (!selectedRows.length) {
      return [];
    } // To remove any unselected items that were selected previously.


    const existingReorderPanelSelections = reorderPanelData.filter(reorderPanelRow => selectedRows.indexOf(reorderPanelRow) !== -1);
    /**
     * Getting the new selections of Edit panel
     * that are not already part of Reorder panel data.
     */

    const newEditPanelSelections = [];
    /**
     * Rows in the ReorderPanel should be shown in the same order of the selected rows within the EditPanel
     * rather than showing the sequence at which the user selects the rows in EditPanel.
     * Eg: If user selects 5th row followed by 1st row and then 2nd, then in the ReorderPanel,
     * the rows should be displayed in the order of 1, 2 and 5 rather than 5, 1 and 2.
     * As the Table component returns the selectedRows based on the order of user selection,
     * looping through the EditPanel data to construct the correct order.
     */

    data.forEach(rowData => {
      if (existingReorderPanelSelections.indexOf(rowData[rowKey]) === -1 && selectedRows.indexOf(rowData[rowKey]) !== -1) {
        newEditPanelSelections.push(rowData[rowKey]);
      }
    });
    return existingReorderPanelSelections.concat(newEditPanelSelections);
  };
  /**
   * @return {array} filteredData - Selected rows from `ReorderPanel`.
   */


  const getReorderSelectedRows = () => {
    const {
      props: {
        data,
        rowKey
      }
    } = children;
    const filteredData = [];
    /**
     * As ListBuilder needs to construct rows on the `reorderPanelData` array,
     * iterating over `data` is necessary.
     */

    reorderPanelData.forEach(item => {
      data.some(dataObj => {
        if (dataObj[rowKey] === item) {
          filteredData.push(dataObj); // Break the loop.

          return true;
        } // Proceed with loop execution.


        return false;
      });
    });
    return filteredData;
  };
  /**
   * @param {string} panel - to identify the panel to be opened.
   */


  const handleOpen = panel => {
    if (panel === EDIT) {
      onToggle(EDIT);
    } else {
      onToggle(REORDER);
    }
  }; // Used to open reorder panel.


  const handleReorder = () => {
    const reorderPanelData = getReorderData();
    setReorderPanelData(reorderPanelData);
    handleOpen(REORDER);
  };
  /**
   * To reset selected items in Reorder panel.
   * @param {boolean} focusTableCell - If true, focuses the last focused cell in table.
   *
   */


  const handleClearSelections = focusTableCell => {
    setReorderSelectedRows([]);
    setDisableMoveDownBottomButtons(true);
    setDisableMoveUpTopButtons(true);
    setFocusTableCell(focusTableCell);
  };
  /**
   * Sets table cell focus after state change made in `handleClearSelections`.
   */


  useEffect(() => {
    if (reorderSelectedRows.length === 0 && disableMoveDownBottomButtons === true && disableMoveUpTopButtons === true) {
      if (focusTableCell && document.activeElement) {
        /**
         * Below is to ensure VO focuses the table cell.
         * Otherwise VO is focusing the ClearSelection text
         * after button click.
         */
        document.activeElement.blur();
      }

      handleTableCellFocus(focusTableCell, reorderPanelNodeRef.current);
      setFocusTableCell(false);
    }
  }, [reorderSelectedRows, disableMoveDownBottomButtons, disableMoveUpTopButtons, focusTableCell]); // Used to open Edit panel when EDIT button is clicked in Reorder panel.

  const handleEdit = () => {
    handleOpen(EDIT);
    handleClearSelections();
  };
  /**
   * A callback to handle closing the keyboard help panel.
   *
   * @param {Boolean} isKeyboardHelpButtonTriggered - Expresses the "triggered" state of the
   *                                                  keyboard help panel's triggering button.
   * @param {*} elementToFocusOnHelpPanelClose - The element to which focus should be given.
   */


  const handleCloseKeyboardHelp = (isKeyboardHelpButtonTriggered, elementToFocusOnHelpPanelClose) => {
    if (elementToFocusOnHelpPanelClose) {
      elementToFocusOnHelpPanelCloseRef.current = elementToFocusOnHelpPanelClose;
    }

    setKeyboardInstructionPanelIsOpen(isKeyboardHelpButtonTriggered);
  };
  /**
   * This method ensures that the focus is reapplied
   * to the last focused element in the Reorder Panel
   * when the Keyboard Help Panel is closed.
   */


  const handleFocusOnReorderPanel = () => {
    const rowId = elementToFocusOnHelpPanelCloseRef.current.parentNode.id;
    /**
     * Wait for the Reorder Panel to exist on the DOM, then focus on the
     * element which had focus prior to opening the keyboard help panel.
     */

    setTimeout(() => {
      // Apply (return) focus to one of the Table cells.
      if (document.getElementById(rowId)) {
        const row = Array.from(elementToFocusOnHelpPanelCloseRef.current.parentNode.children); // Get the child node that has focus.

        const activeCell = row.map(child => child.tabIndex).indexOf(0); // Ensure that focus is given to the currently selected cell.

        document.getElementById(rowId).children[activeCell].tabIndex = 0;
        document.getElementById(rowId).children[activeCell].focus();
      } // Apply (return) focus to one of the buttons in the panel.
      else if (elementToFocusOnHelpPanelCloseRef.current.id) {
        document.getElementById(`${elementToFocusOnHelpPanelCloseRef.current.id}`).focus();
      }
    }, 10); // eslint-disable-line no-magic-numbers
  };
  /**
   * @param {string} panel - used to know which panel to be closed.
   */


  const handleClose = panel => {
    if (panel === REORDER) {
      handleClearSelections();
    }

    if (keyboardInstructionPanelIsOpen) {
      handleCloseKeyboardHelp(false);
    } else {
      setSelectedRows(lastSavedSelectedRows);
      setReorderPanelData(lastSavedReorderPanelData);
      setFindByLookUpString("");
      setFindByFilteredData(null);
      setKeyboardInstructionPanelIsOpen(false);
      onClose(lastSavedReorderPanelData);
    }

    minSelectedRef.current = false;
    maxSelectedRef.current = false;
  };
  /**
   * Sets focus to Reorder Panel after state change made in `handleClose`.
   */


  useEffect(() => {
    if (!keyboardInstructionPanelIsOpen && elementToFocusOnHelpPanelCloseRef.current) {
      handleFocusOnReorderPanel();
    }
  }, [keyboardInstructionPanelIsOpen]);
  /**
   * @param {string} panel - to know from which panel save is performed.
   */

  const handleSave = panel => {
    const selectedData = getReorderData();
    setLastSavedReorderPanelData(selectedRows);
    setLastSavedSelectedRows(selectedData.slice());
    setReorderPanelData(selectedData.slice());
    setFindByLookUpString("");
    setFindByFilteredData(null);

    if (panel === REORDER) {
      handleClearSelections();
    }

    afterSave(selectedData);
  };
  /**
   * @param {array} selectedRows - holds array of selected row `rowKey` values.
   * Executes when selections are made in Edit panel.
   */


  const handleEditSelect = _ref2 => {
    let {
      selectedRows
    } = _ref2;
    setSelectedRows(selectedRows);
  };
  /**
   * @param {array} reorderPanelData - array of Reorder panel data objects.
   * @param {array} selectedRows - array of selected row `rowKey` values.
   * Method that enables/disables
   * the move action buttons to make reordering the selections possible.
   */


  const toggleShuffleButtonsState = (reorderPanelData, selectedRows) => {
    const firstRowSelected = selectedRows.indexOf(reorderPanelData[0]) !== -1;
    const lastRowSelected = selectedRows.indexOf(reorderPanelData[reorderPanelData.length - 1]) !== -1;
    let localDisableMoveDownBottomButtons = disableMoveDownBottomButtons,
        localDisableMoveUpTopButtons = disableMoveUpTopButtons;

    if (firstRowSelected && lastRowSelected) {
      localDisableMoveDownBottomButtons = true;
      localDisableMoveUpTopButtons = true;
    } else if (firstRowSelected) {
      localDisableMoveDownBottomButtons = false;
      localDisableMoveUpTopButtons = true;
    } else if (lastRowSelected) {
      localDisableMoveUpTopButtons = false;
      localDisableMoveDownBottomButtons = true;
    } else if (selectedRows.length) {
      localDisableMoveDownBottomButtons = false;
      localDisableMoveUpTopButtons = false;
    } else {
      localDisableMoveDownBottomButtons = true;
      localDisableMoveUpTopButtons = true;
    }

    return {
      disableMoveUpTopButtons: localDisableMoveUpTopButtons,
      disableMoveDownBottomButtons: localDisableMoveDownBottomButtons
    };
  };
  /**
   * @param {array} selectedRows - array of selected row `rowKey` values.
   * Executes when selections are made in Reorder panel.
   */


  const handleReorderSelect = _ref3 => {
    let {
      selectedRows
    } = _ref3;
    const shuffleButtonsState = toggleShuffleButtonsState(reorderPanelData, selectedRows);
    setReorderSelectedRows([...selectedRows]);
    setDisableMoveUpTopButtons(shuffleButtonsState.disableMoveUpTopButtons);
    setDisableMoveDownBottomButtons(shuffleButtonsState.disableMoveDownBottomButtons);
  };
  /**
   * @param {number} selectedRowsCount - number of rows selected in Edit panel.
   * To validate the type of `feedback` message to be displayed.
   */


  const editPanelRowsSelectionValidator = selectedRowsCount => {
    if (minSelection === 0 && maxSelection === 0) {
      return;
    }
    /**
     * Ensures minimum selections are made, at least once, before showing error.
     */


    if (minSelection <= selectedRowsCount) {
      minSelectedRef.current = true;
    }
    /**
     * Ensures maximum selections are made, at least once, before showing error.
     */


    if (maxSelection <= selectedRowsCount) {
      maxSelectedRef.current = true;
    }
  };
  /**
   * @param {string} action - to know if selectedRows need to be moved to TOP or BOTTOM.
   * Performs move to TOP/BOTTOM actions in Reorder panel.
   */


  const handleMoveTopBottom = action => {
    const selected = [];
    const unselected = [];
    reorderPanelData.forEach(item => {
      if (reorderSelectedRows.indexOf(item) !== -1) {
        selected.push(item);
      } else {
        unselected.push(item);
      }
    });
    const reorderedData = action === TOP ? selected.concat(unselected) : unselected.concat(selected);
    const shuffleButtonsState = toggleShuffleButtonsState(reorderedData, reorderSelectedRows);
    setReorderPanelData(reorderedData);
    setDisableMoveUpTopButtons(shuffleButtonsState.disableMoveUpTopButtons);
    setDisableMoveDownBottomButtons(shuffleButtonsState.disableMoveDownBottomButtons);
  };
  /**
   * @param {string} action - to know if the selectedRows need to be moved UP or DOWN.
   * Performs move UP/DOWN actions in Reorder panel.
   */


  const handleMoveUpDown = action => {
    const localReorderPanelData = reorderPanelData.slice();
    const reorderData = [];

    if (action !== UP) {
      localReorderPanelData.reverse();
    }

    localReorderPanelData.forEach(item => {
      if (reorderSelectedRows.indexOf(item) !== -1) {
        reorderData.splice(reorderData.length - 1, 0, item);
      } else {
        reorderData.push(item);
      }
    });
    const reorderedData = action === UP ? reorderData : reorderData.reverse();
    const shuffleButtonsState = toggleShuffleButtonsState(reorderedData, reorderSelectedRows);
    setReorderPanelData(reorderedData);
    setDisableMoveUpTopButtons(shuffleButtonsState.disableMoveUpTopButtons);
    setDisableMoveDownBottomButtons(shuffleButtonsState.disableMoveDownBottomButtons);
  };
  /**
   * @description This method saves the value of the input field.
   * @param {Object} object
   */


  const handleValueChange = _ref4 => {
    let {
      value
    } = _ref4;
    setFindByLookUpString(value);
  };
  /**
   * @description This method clears the input field and resets table data.
   */


  const handleReset = () => {
    setFindByLookUpString("");
    setFindByFilteredData(null);
  };
  /**
   * @description This method clears the input field and then sets focus to it.
   */


  const handleClear = () => {
    setFindByLookUpString("");
  };

  useEffect(() => {
    if (findByLookUpString === "" && inputElementRef.current) {
      inputElementRef.current.focus();
    }
  }, [findByLookUpString]);
  /**
   * @description This method filters the records that match the search string.
   */

  const handleFilter = () => {
    const tableData = children.props.data;
    const lookUpText = findByLookUpString.toLowerCase().trim();
    const filteredData = tableData.filter(record => {
      for (const recordKey in record) {
        if (record.hasOwnProperty(recordKey)) {
          const recordValue = record[recordKey].toString().toLowerCase().trim();

          if (recordValue.indexOf(lookUpText) !== -1) {
            return true;
          }
        }
      }

      return false;
    });
    setFindByFilteredData(filteredData);
  };

  editPanelRowsSelectionValidator(selectedRows.length);
  return _jsxs(React.Fragment, {
    children: [openEdit && _jsx(EditPanel, {
      selectedRows: selectedRows,
      openEditPanel: openEdit,
      elementToFocusOnClose: elementToFocusOnClose,
      maxSelection: maxSelection,
      minSelection: minSelection,
      minSelected: minSelectedRef.current,
      maxSelected: maxSelectedRef.current,
      customStrings: strings,
      onEditSelect: handleEditSelect,
      onReorderOpen: handleReorder,
      onClose: handleClose,
      afterSave: handleSave,
      findBy: findBy,
      fieldValue: findByLookUpString,
      handleValueChange: handleValueChange,
      fieldRef: setRootRef,
      handleClear: handleClear,
      resetHandler: handleReset,
      findByContainerAriaLabel: findByContainerAriaLabel,
      editTableData: findByFilteredData || children.props.data,
      dataIsFiltered: findByFilteredData !== null,
      filterHandler: handleFilter,
      editPanelHeaderDockAriaLabel: editPanelHeaderDockAriaLabel,
      panelWidth: panelWidth,
      disableButtons: disableButtons,
      children: children
    }), openReorder && !keyboardInstructionPanelIsOpen && _jsx(ReorderPanel, {
      reorderPanelEditButtonHidden: reorderPanelEditButtonHidden,
      reorderSelectedRows: reorderSelectedRows,
      reorderPanelData: reorderPanelData,
      openReorderPanel: openReorder,
      elementToFocusOnClose: elementToFocusOnClose,
      disableMoveUpTopButtons: disableMoveUpTopButtons,
      disableMoveDownBottomButtons: disableMoveDownBottomButtons,
      customStrings: strings,
      onClearSelections: handleClearSelections,
      onReorderSelect: handleReorderSelect,
      onMoveTopBottom: handleMoveTopBottom,
      onMoveUpDown: handleMoveUpDown,
      onEditOpen: handleEdit,
      afterSave: handleSave,
      onClose: handleClose,
      reorderData: getReorderSelectedRows(),
      reorderTableName: reorderTableName,
      reorderPanelHeaderDockAriaLabel: reorderPanelHeaderDockAriaLabel,
      ref: reorderPanelNodeRef,
      panelWidth: panelWidth,
      onHandleCloseKeyboardHelp: handleCloseKeyboardHelp,
      disableButtons: disableButtons,
      children: children
    }), keyboardInstructionPanelIsOpen && _jsx(KeyboardHelpPanel, {
      customStrings: strings,
      onClose: handleClose,
      onHandleCloseKeyboardHelp: handleCloseKeyboardHelp,
      elementToFocusOnClose: elementToFocusOnClose
    })]
  });
}

ListBuilder.propTypes = process.env.NODE_ENV !== "production" ? {
  /** aria-label for Edit Panel header dock */
  editPanelHeaderDockAriaLabel: PropTypes.string.isRequired,

  /**
   * This prop identifies the element that should receive
   * focus after Edit or Reorder panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /**
   * Executed whenever user shuffles between Edit/Reorder panels.
   * It will be called with the following:
   *
   * <ul>
   *  <li>
   *    `EDIT`: if EDIT panel is opened from Reorder panel.
   *  </li>
   *  <li>
   *    `REORDER`: if REORDER panel is opened from Edit panel.
   *  </li>
   * </ul>
   */
  onToggle: PropTypes.func.isRequired,

  /** Indicates if Edit panel is in open/close state. True if open. */
  openEdit: PropTypes.bool.isRequired,

  /** Indicates if Reorder panel is in open/close state. True if open. */
  openReorder: PropTypes.bool.isRequired,

  /** aria-label for Reorder Panel header dock */
  reorderPanelHeaderDockAriaLabel: PropTypes.string.isRequired,

  /**
   * It will be read along with Instruction text for Reorder Table.
   */
  reorderTableName: PropTypes.string.isRequired,

  /**
   * Callback to be executed after click of `Save` button in Edit/Reorder panels.
   * It will be called with the following argument:
   *
   * <ul>
   *  <li>
   *    `savedData`: array of unique id's.
   *  </li>
   * </ul>
   */
  afterSave: PropTypes.func,

  /**
   * This is what will be displayed inside the Panel.
   * It can only be of type Table and it accepts these props:
   *  `data`, `rowKey`, `columns`, `selectable`, `sortable`, `expandable`.
   */
  children: validChild([Table]),

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /** aria-label for Panel's close button when the ListBuilder's Edit Panel is open. */
    closeButtonAriaLabelEditPanel: PropTypes.string,

    /** aria-label for Panel's close button when the ListBuilder's Keyboard Help Panel is open. */
    closeButtonAriaLabelKeyboardHelpPanel: PropTypes.string,

    /** aria-label for Panel's close button when the ListBuilder's Reorder Panel is open. */
    closeButtonAriaLabelReorderPanel: PropTypes.string,

    /** Title of the Edit panel. */
    editPanelTitle: PropTypes.string,

    /** Title of the Reorder panel. */
    reorderPanelTitle: PropTypes.string,

    /** Save button label that will be displayed in Edit and Reorder panels. */
    saveButtonLabel: PropTypes.string,

    /** Cancel button label that will be displayed in Edit and Reorder panels. */
    cancelButtonLabel: PropTypes.string,

    /** Edit button label. */
    editButtonLabel: PropTypes.string,

    /** Reorder button label. */
    reorderButtonLabel: PropTypes.string,

    /** General message to be shown in Feedback component. */
    feedbackInstruction: PropTypes.string,

    /** Error message to display when the minimum selections have not been made. */
    feedbackMinSelectionError: PropTypes.string,

    /** Error message to display when the maximum selections have been exceeded. */
    feedbackMaxSelectionError: PropTypes.string,

    /** `Move to top` button of Reorder panel aria-label text. */
    moveToTopAriaLabel: PropTypes.string,

    /** `Move up` button of Reorder panel aria-label text. */
    moveUpAriaLabel: PropTypes.string,

    /** `Move down` of Reorder panel button aria-label text. */
    moveDownArialLabel: PropTypes.string,

    /** `Move to bottom` button of Reorder panel aria-label text. */
    moveToBottomArialLabel: PropTypes.string,

    /**
     * Render prop for the selected rows count message in the Edit Panel.
     * It will be called with the following arguments:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected in the Edit Panel.
     *  </li>
     *  <li>
     *    `item`: This holds the singular or plural of item, based on `selectedRowsCount`.
     *  </li>
     * </ul>
     */
    editPanelSelectedRowsMessage: PropTypes.func,

    /**
     * Render prop for the selected rows count message in the Reorder Panel
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected in the Reorder Panel.
     *  </li>
     * </ul>
     */
    reorderPanelSelectedRowsMessage: PropTypes.func,

    /** aria-label for the toolbar. */
    toolBarAriaLabel: PropTypes.string,

    /**
     * Text to be announced by the screen readers when "Go" button is triggered.
     * Applicable only when `FindBy` is configured.
     * Defaults to "${filteredRecordCount} items found".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `filteredRecordCount`: Number of rows filtered in the Edit Panel.
     *  </li>
     * </ul>
     */
    findAnnouncement: PropTypes.func,

    /**
     * Text to be announced by the screen readers when "Reset" button is triggered.
     * Applicable only when `FindBy` is configured.
     * Defaults to "Results and search settings have been reset".
     */
    resetAnnouncement: PropTypes.string,

    /**
     * aria-label which will be applied to "Go" button.
     * Defaults to "Go search data".
     */
    findButtonAriaLabel: PropTypes.string,

    /**
     * aria-label which will be applied to "Reset" button.
     * Defaults to "Reset results and clear".
     */
    resetButtonAriaLabel: PropTypes.string,

    /**
     * aria-label which will be applied to "X(Clear Selections)" button in Reorder panel.
     * Defaults to "Clear Selections".
     */
    clearSelectionAriaLabel: PropTypes.string,

    /**
     * Aria description for "X(Clear Selections)" button in Reorder panel.
     * Defaults to "Resets selected items".
     */
    clearSelectionDescription: PropTypes.string,

    /**
     * aria-label for Selection Controls container in Reorder panel.
     * Defaults to "Selection controls".
     */
    selectionControlAriaLabel: PropTypes.string,

    /**
     * Aria description for Move to top button in Reorder panel.
     * Defaults to "Move selected items to top of list".
     */
    moveToTopDescription: PropTypes.string,

    /**
     * Aria description for Move up button in Reorder panel.
     * Defaults to "Move selected items up one position".
     */
    moveUpDescription: PropTypes.string,

    /**
     * Aria description for Move down button in Reorder panel.
     * Defaults to "Move selected items down one position".
     */
    moveDownDescription: PropTypes.string,

    /**
     * Aria description for Move to bottom button in Reorder panel.
     * Defaults to "Move selected items to bottom of list".
     */
    moveToBottomDescription: PropTypes.string,

    /**
     * Text to be announced by the screen readers when "Move to top" button is triggered.
     * Defaults to "${selectedRowsCount} items moved to top of list".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected.
     *  </li>
     * </ul>
     */
    moveToTopAnnouncement: PropTypes.func,

    /**
     * Text to be announced by the screen readers when "Move up" button is triggered.
     * Defaults to "${selectedRowsCount} items moved up one position".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected.
     *  </li>
     * </ul>
     */
    moveUpAnnouncement: PropTypes.func,

    /**
     * Text to be announced by the screen readers when "Move down" button is triggered.
     * Defaults to "${selectedRowsCount} items moved down one position".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected.
     *  </li>
     * </ul>
     */
    moveDownAnnouncement: PropTypes.func,

    /**
     * Text to be announced by the screen readers when "Move to bottom" button is triggered.
     * Defaults to "${selectedRowsCount} items moved to bottom of list".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected.
     *  </li>
     * </ul>
     */
    moveToBottomAnnouncement: PropTypes.func,

    /**
     * An object containing the strings used for keyboard help panel.
     * Defaults to the following strings:
     *
     * <ul>
     *  <li>`action`: "Action".</li>
     *  <li>
     *    `cellNavigation`:
     *    <ul>
     *      <li>
     *        `up`: <code>{actionText: "Move up one cell", shortcutText: "Up arrow"}</code>
     *      </li>
     *      <li>
     *        `down`: <code>{actionText: "Move down one cell", shortcutText: "Down arrow"}</code>
     *      </li>
     *      <li>
     *        `left`: <code>{actionText: "Move left one cell", shortcutText: "Left arrow"}</code>
     *      </li>
     *      <li>
     *        `right`: <code>{actionText: "Move right one cell", shortcutText: "Right arrow"}</code>
     *      </li>
     *    </ul>
     *  </li>
     *  <li>`
     *    clearSelectedRows`: <code>{actionText: "Clear selected rows", shortcutText: "Shift + X"}</code>
     *  </li>
     *  <li>`close`: "Close"</li>
     *  <li>
     *    `moveSelectedRows`:
     *    <ul>
     *      <li>`up`:
     *        <code>{actionText: "Move selected rows up one row", shortcutText: "Shift + up arrow"}</code>
     *      </li>
     *      <li>`down`:
     *        <code>
     *          {actionText: "Move selected rows down one row", shortcutText: "Shift + down arrow"}
     *        </code>
     *      </li>
     *      <li>`toTop`:
     *        <code>{actionText: "Move selected rows to top of table", shortcutText:
     *        "Shift + control + up arrow"}</code>
     *      </li>
     *      <li>`toBottom`:
     *        <code>{actionText: "Move selected rows to bottom of table", shortcutText:
     *        "Shift + control + down arrow"}</code>
     *      </li>
     *    </ul>
     *  </li>
     *  <li>`navigationInstructions`:
     *    "You can use the keyboard to navigate the table using the following keys:"
     *  </li>
     *  <li>`panelShowHideInstructions`:
     *    "ALT+K (Windows) / Option+K (Mac) for these keyboard instructions."
     *  </li>
     *  <li>`panelTitle`: "Keyboard Help"</li>
     *  <li>`selectedInstructions`: "With rows selected:"</li>
     *  <li>`shortcutKey`: "Shortcut Key"</li>
     *  <li>
     *    `toggle`:
     *    <ul>
     *      <li>`selectMode`: <code>{actionText: "Toggle select mode", shortcutText:
     *      "ALT+W (Windows) / Option+W (Mac)"}</code>
     *      </li>
     *      <li>`selectedRow`: <code>{actionText: "Select the row", shortcutText: "Space bar"}</code>
     *      </li>
     *    </ul>
     *  </li>
     * </ul>
     */
    keyboardHelp: PropTypes.shape({
      action: PropTypes.string,
      cellNavigation: PropTypes.shape({
        up: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        down: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        left: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        right: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        })
      }),
      clearSelectedRows: PropTypes.shape({
        actionText: PropTypes.string,
        shortcutText: PropTypes.string
      }),
      close: PropTypes.string,
      moveSelectedRows: PropTypes.shape({
        up: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        down: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        toTop: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        toBottom: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        })
      }),
      navigationInstructions: PropTypes.string,
      panelShowHideInstructions: PropTypes.string,
      panelTitle: PropTypes.string,
      selectedInstructions: PropTypes.string,
      shortcutKey: PropTypes.string,
      toggle: PropTypes.shape({
        selectMode: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        }),
        selectedRow: PropTypes.shape({
          actionText: PropTypes.string,
          shortcutText: PropTypes.string
        })
      })
    })
  }),

  /**
   * Configuration to disable footer `Edit`, `Reorder`, `Save` buttons.
   * Defaults to empty array.
   * It accepts an array of strings for which we have statics, namely:
   * ListBuilder.REORDERPANEL.EDIT
   * ListBuilder.REORDERPANEL.SAVE
   * ListBuilder.EDITPANEL.SAVE
   * ListBuilder.EDITPANEL.REORDER
   * @ignore
   */
  disableButtons: PropTypes.arrayOf(PropTypes.string),

  /**
   * Enables the FindBy component in the edit panel.
   * This is rendered between the FeedBack and Table components.
   */
  findBy: PropTypes.bool,

  /** aria-label which will be applied to the FindBy container. */
  findByContainerAriaLabel: requiredIf(PropTypes.string, (props, propName) => props.findBy && !props[propName], "if `findBy` is configured"),

  /** Maximum number of selections that can be made in Edit panel. */
  maxSelection: PropTypes.number,

  /** Minimun number of selections to be made in Edit panel. */
  minSelection: PropTypes.number,

  /**
   * Callback to be executed on click of Close icon button/Cancel button in Edit/Reorder panels.
   * It will be called with the following argument:
   *
   * <ul>
   *  <li>
   *    `lastSavedData`: array of unique id's.
   *  </li>
   * </ul>
   */
  onClose: PropTypes.func,

  /**
   * The width of the ListBuilder Panels at a desktop breakpoint.
   * Must include the unit type. i.e. 200px or 40%.
   */
  panelWidth: PropTypes.string,

  /** Prop to hide the `edit` button inside the Reorder Panel. */
  reorderPanelEditButtonHidden: PropTypes.bool
} : {};
ListBuilder.TYPES = {
  EDIT: "EDIT",
  REORDER: "REORDER"
};
ListBuilder.REORDERPANEL = {
  EDIT,
  SAVE: REORDERSAVE
};
ListBuilder.EDITPANEL = {
  REORDER,
  SAVE: EDITSAVE
};
ListBuilder.defaultProps = {
  children: null,
  onClose: null,
  afterSave: null,
  minSelection: 0,
  maxSelection: 0,
  customStrings: null,
  panelWidth: null,
  findBy: false,
  findByContainerAriaLabel: null,
  reorderPanelEditButtonHidden: false,
  disableButtons: []
};
ListBuilder.displayName = "ListBuilder";
export default ListBuilder;